import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  Injectable,
  ViewContainerRef
} from '@angular/core'

import { NotificationComponent } from '../../components/notification/notification.component'
import { I18nService } from '../i18n/i18n.service'
import { OverlayHostService } from '../overlay-host/overlay-host.service'

/**
 * @description
 * The types of notification available.
 *
 * @docsCategory services
 * @docsPage NotificationService
 */
export type NotificationType = 'info' | 'success' | 'error' | 'warning'

/**
 * @description
 * Configuration for a toast notification.
 *
 * @docsCategory services
 * @docsPage NotificationService
 */
export interface ToastConfig {
  message: string
  translationVars?: { [key: string]: string | number }
  type?: NotificationType
  duration?: number
  action?: () => void
}

// How many ms before the toast is dismissed.
const TOAST_DURATION = 5000
const TOAST_ERROR_DURATION = 7000

/**
 * @description
 * Provides toast notification functionality.
 *
 * @example
 * ```ts
 * class MyComponent {
 *   constructor(private notificationService: NotificationService) {}
 *
 *   save() {
 *     this.notificationService
 *         .success(_('asset.notify-create-assets-success'), {
 *           count: successCount,
 *         });
 *   }
 * }
 *
 * @docsCategory services
 * @docsPage NotificationService
 * @docsWeight 0
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private get hostView(): Promise<ViewContainerRef> {
    return this.overlayHostService.getHostView()
  }

  private openToastRefs: Array<{
    ref: ComponentRef<NotificationComponent>
    timerId: any
  }> = []

  constructor(
    private i18nService: I18nService,
    private resolver: ComponentFactoryResolver,
    private overlayHostService: OverlayHostService,
    private appRef: ApplicationRef
  ) {}

  /**
   * @description
   * Display a success toast notification
   */
  success(
    message: string,
    translationVars?: { [key: string]: string | number }
  ): void {
    this.notify({
      message,
      translationVars,
      type: 'success'
    })
  }

  /**
   * @description
   * Display an info toast notification
   */
  info(
    message: string,
    translationVars?: { [key: string]: string | number }
  ): void {
    this.notify({
      message,
      translationVars,
      type: 'info'
    })
  }

  /**
   * @description
   * Display a warning toast notification
   */
  warning(
    message: string,
    translationVars?: { [key: string]: string | number }
  ): void {
    this.notify({
      message,
      translationVars,
      type: 'warning'
    })
  }

  /**
   * @description
   * Display an error toast notification
   */
  error(
    message: string,
    translationVars?: { [key: string]: string | number }
  ): void {
    this.notify({
      message,
      translationVars,
      type: 'error'
    })
  }

  /**
   * @description
   * Display a toast notification.
   */
  notify(config: ToastConfig): Promise<boolean> {
    try {
      this.createToast(config)
      return Promise.resolve(true)
    } catch (error) {
      console.error('Notification error:', error)
      return Promise.resolve(false)
    }
  }

  /**
   * Load a ToastComponent into the DOM host location.
   */
  private async createToast(config: ToastConfig): Promise<void> {
    const toastFactory = this.resolver.resolveComponentFactory(
      NotificationComponent
    )

    let ref: ComponentRef<NotificationComponent>
    let hostView: ViewContainerRef | null = null

    try {
      hostView = await this.hostView
    } catch (e) {
      console.error('Failed to create toast:', e)
    }

    if (hostView) {
      ref = hostView.createComponent(toastFactory)
    } else {
      const injector =
        (window as any).ng?.getInjector?.(document.body) || undefined
      ref = toastFactory.create(injector)

      this.appRef.attachView(ref.hostView)

      document.body.appendChild(ref.location.nativeElement)
    }

    const toast = ref.instance
    toast.type = config.type || 'info'
    toast.message = this.i18nService.translate(config.message)
    toast.translationVars = this.translateTranslationVars(
      config.translationVars || {}
    )

    if (config.action) {
      toast.registerOnClickFn(config.action)
    }

    ref.changeDetectorRef.detectChanges()

    setTimeout(() => {
      toast.isVisible = true
      ref.changeDetectorRef.detectChanges()
    }, 50)

    const dismissFn = this.createDismissFunction(ref)

    const defaultDuration =
      config.type === 'error' || config.type === 'warning'
        ? TOAST_ERROR_DURATION
        : TOAST_DURATION

    const timer = setTimeout(() => {
      dismissFn()
    }, config.duration || defaultDuration)

    this.openToastRefs.push({ ref, timerId: timer })
    this.calculatePositions()
  }

  /**
   * Returns a function which will destroy the toast component and
   * remove it from the openToastRefs array.
   */
  private createDismissFunction(
    ref: ComponentRef<NotificationComponent>
  ): () => void {
    return () => {
      const toast: NotificationComponent = ref.instance
      const index = this.openToastRefs.map((o) => o.ref).indexOf(ref)

      if (index === -1) return

      if (this.openToastRefs[index]) {
        clearTimeout(this.openToastRefs[index].timerId)
      }

      toast.fadeOut().then(() => {
        try {
          if (this.appRef.components.includes(ref as any)) {
            this.appRef.detachView(ref.hostView)
          }
          ref.destroy()
          this.openToastRefs.splice(index, 1)
          this.calculatePositions()
        } catch (err) {
          console.error('Error during toast dismissal:', err)
        }
      })
    }
  }

  /**
   * Calculate and set the top offsets for each of the open toasts.
   */
  private calculatePositions(): void {
    let cumulativeHeight = 10

    this.openToastRefs.forEach((obj) => {
      const toast: NotificationComponent = obj.ref.instance
      toast.offsetTop = cumulativeHeight
      obj.ref.changeDetectorRef.detectChanges()
      cumulativeHeight += toast.getHeight() + 6
    })
  }

  private translateTranslationVars(translationVars: {
    [key: string]: string | number
  }): {
    [key: string]: string | number
  } {
    for (const [key, val] of Object.entries(translationVars)) {
      if (typeof val === 'string') {
        translationVars[key] = this.i18nService.translate(val)
      }
    }
    return translationVars
  }
}
